import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getServicesQueryOptions } from '@/services/api';
import type { ObjectTypeResult } from '@/services/auth/types';
import { CLIENT_SIDE_RESULT_CAP, SHARED_QUERY_OPTIONS } from './constants';
import { filterObjectTypes, mapObjectTypeToSearchResult } from './utils/integration-results';
import type { SearchSource } from './utils/source-bookkeeping';

type UseObjectTypesSourceOptions = {
  workspaceId: number;
  enabled: boolean;
  query: string;
};

type ObjectTypesSource = SearchSource<ObjectTypeResult>;

export function useObjectTypesSource({ workspaceId, enabled, query }: UseObjectTypesSourceOptions): ObjectTypesSource {
  const objectTypesQuery = useQuery(
    getServicesQueryOptions(
      'getObjectTypes',
      { params: { environment_id: workspaceId } },
      {
        enabled,
        ...SHARED_QUERY_OPTIONS,
        select: (data) => data.object_types,
      },
    ),
  );

  const results = useMemo(
    () =>
      filterObjectTypes(objectTypesQuery.data ?? [], query, CLIENT_SIDE_RESULT_CAP).map((objectType) =>
        mapObjectTypeToSearchResult(objectType, workspaceId),
      ),
    [objectTypesQuery.data, query, workspaceId],
  );

  return { ...objectTypesQuery, results };
}
